import { useEffect, createContext } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Modal,
  Space,
  Spin,
  Card,
  Row,
  Col,
  Flex,
  Dropdown,
  MenuProps,
} from "antd";

import { DownOutlined } from "@ant-design/icons";

import ObjectForm from "components/Admin/ObjectForm";

import { RootState } from "Redux/store";
import { fetchMasterPlan, createMasterPlan, updateMasterPlan, deleteMasterplan } from "Redux/Admin/GoalsManager/MasterPlans/action";

import { settings, initialSettings, initialValues } from './const';
import { ADMIN_PATH } from "utils/routes";

import type { IMasterPlan } from 'Redux/Admin/GoalsManager/MasterPlans/type';

const Masterplan = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const { entities, loading, error } = useSelector((state: RootState) => state.fetchMasterPlans);

  const [modal, contextHolder] = Modal.useModal();

  const ReachableContext = createContext<string | null>(null);

  useEffect(() => {
    if (id && !isNew) {
      dispatch(fetchMasterPlan(id));
    }
  }, [dispatch, isNew, id]);

  const onFinish = (values: any) => {
    if (isNew) {
      dispatch(createMasterPlan(values, handleCreate));
    }
    else if (id) {
      dispatch(updateMasterPlan(values, id));
    }
  };

  const handleCreate = (masterPlan: IMasterPlan) => {
    if (masterPlan && masterPlan.id) {
      navigate(`${ADMIN_PATH}/goals/journeys/masterplans/${masterPlan.id}`);
    }
  }

  if (!id) {
    navigate(`${ADMIN_PATH}/goals/journeys/masterplans`, { replace: true });
    return null;
  }

  const masterPlan = isNew ? initialValues : entities[id];

  if (!masterPlan) {
    return <Spin />;
  }

  const menuItems: MenuProps['items'] = [
    {
      label: 'View Areas',
      key: '1',
    },
    {
      label: 'Delete Masterplan',
      key: '2',
      danger: true,
    }
  ];

  const modalConfig = {
    title: 'Delete the masterplan?',
    content: <span>Are you sure you want to delete the masterplan <b>{masterPlan.id}</b>?</span>,
    confirmLoading: true
  };

  const handleMenuClick: MenuProps['onClick'] = async (e) => {
    if (e.key === '1') {
      navigate(`${ADMIN_PATH}/goals/journeys/user-areas/?masterplan=${id}`);
    }
    else if (e.key === '2') {
      const confirmed = await modal.confirm(modalConfig);

      if (confirmed) {
        dispatch(deleteMasterplan(id, () => {
          navigate(`${ADMIN_PATH}/goals/journeys/masterplans`, { replace: true });
        }));
      }
    }
  };

  const menuProps = {
    items: menuItems,
    onClick: handleMenuClick,
  };

  return (
    <ReachableContext.Provider value="Light">
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Row>
          <Col span={12}>
            <Flex
              gap="middle"
              align="center"
              justify="space-between"
              style={{ marginBottom: 16 }}
            >
              <Link to={`${ADMIN_PATH}/goals/journeys/masterplans`}>Back</Link>
            </Flex>

            <Card title={`${isNew ? 'Create' : '' } Masterplan`} extra={
              isNew ? null : <Dropdown menu={menuProps} placement="bottom">
                <Button>
                  <Space>
                    Manage Masterplan
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            }>
              <ObjectForm
                data={masterPlan}
                errorData={error}
                isNew={isNew}
                settings={isNew ? initialSettings : settings}
                loading={loading}
                onFinish={onFinish}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {contextHolder}
    </ReachableContext.Provider>
  );
}

export default Masterplan;
